/**
 * THE NAVIGATION MODEL — one list, rendered by every menu and by the site guide.
 *
 * 🚨 WHY THIS FILE EXISTS (2026-08-15, owner: "site is too many pages and unorganized, users don't
 * know where to go"). The site has 200 page routes across 148 top-level directories, and the
 * navigation was spread across THREE hand-written lists that all disagreed:
 *
 *     WatchlistPane.tsx NAV_GROUPS : 64 items, 8 groups  <- THE ONE USERS ACTUALLY SEE
 *     Navbar.tsx desktop sidebar   : 30 items, flat
 *     Navbar.tsx mobile drawer     : 42 items, flat
 *
 * 35 items were in the rendered sidebar and in NEITHER Navbar list. Nothing checked that they
 * agreed, so the failure was always silent: the page works, the route resolves, the link is in the
 * bundle, and no one can find it. WatchlistPane's own comment recorded the lesson the hard way after
 * /api-docs sat invisible for hours: "Adding a page to Navbar.tsx alone ships nothing."
 *
 * ⚠ IT WAS PROVEN THE HARD WAY AGAIN. A first pass extracted only the two Navbar lists, typechecked,
 * passed its tests and deployed green — and a DOM query on prod found ZERO of its headings, because
 * the Navbar sidebar does not render. Verify machinery FIRES: ask the rendered page what it contains.
 *
 * ⭐ THE RULE: a page reachable from the navigation appears HERE, once. Every menu maps over this, so
 * they cannot diverge again. Adding a link to a component by hand re-creates the bug.
 *
 * ⭐ `desc` IS NOT DECORATION. It is the one-line plain-English answer to "what is this page for",
 * and the site guide is generated from it, so a page added to the nav documents itself. Five separate
 * guide pages existed on 2026-08-15 and NONE documented /discover, which is why its label ("Discover",
 * on a stock site) had been reading as stock discovery when it is the member activity feed.
 *
 * 🚨 NEVER CHANGE OR REMOVE A `key`. WatchlistPane persists each user's sidebar order and hidden
 * items against these exact strings in localStorage (`dt_ws_nav_v2`). Renaming one silently resets
 * that user's customisation; deleting one removes the page from the site for everybody.
 *
 * ⚠ NO EM DASHES IN `label` OR `desc`. Both render as user-facing site copy.
 */
import type { LucideIcon } from 'lucide-react'
// Icons load lazily (register mobile-first-paint-1021): see ./navIcon.ts. Add new ones to ./navIconSet.ts.
import { I } from './navIcon'

/**
 * The navigation, exactly as it renders. Groups are ordered; a null label is the ungrouped run at the
 * top. `as const` is load-bearing: NavKey is derived from it, so a typo in a key is a type error
 * rather than a silently reset user preference.
 */
export const SIDEBAR_GROUPS = [
  // ⭐ GROUPED BY INTENT, NOT BY INVENTORY (2026-09-21). The old model had a 21-item "Markets" bucket
  // that mixed discovery, research tools, money-flow and community next to a separate 16-item
  // "Movers & Scanners" that overlapped it: scannable by nobody. Every item and its exact `key` is
  // preserved; only the grouping and order changed, so it reshapes the top nav, the WatchlistPane rail
  // and ⌘K search all at once (they all map over this) with no page added or removed.
  {
    // The everyday run, ungrouped at the very top. The six a returning trader opens without thinking,
    // plus the account link. News and Due Diligence moved to "Research a stock"; Top Stocks and Live
    // Radar moved up here from the old Markets bucket because they are daily destinations, not browsing.
    id: 'core', label: null, items: [
      { key: 'home', href: '/', label: 'Home', Icon: I.Home,
        desc: 'The front page: what the market is doing right now and where to go next.' },
      { key: 'pulse', href: '/pulse', label: 'The Pulse', Icon: I.Zap,
        desc: 'Your dashboard: your watchlist today, early catches on your names, and what you missed, with market news and the community alongside.' },
      { key: 'alerts', href: '/alerts', label: 'Alerts', Icon: I.Bell,
        desc: 'Your alert feed, and the history of what each alert did after it fired.' },
      { key: 'top-stocks', href: '/top-stocks', label: 'Top Stocks Today', Icon: I.Trophy,
        desc: 'The strongest names in the market right now, ranked across five timeframes and refreshed while the market is open. Start here if you want one answer.' },
      { key: 'best-bets', href: '/best-bets', label: 'Best Bets', Icon: I.Target,
        desc: 'The few names most likely to keep running right now, ranked by a measured likelihood signal with the odds of a clean move and the downside shown on every pick.' },
      { key: 'radar', href: '/radar', label: 'Live Radar', Icon: I.Radar,
        desc: 'The stocks the Watcher is flagging right now, ranked by how many independent signals line up, with the move since each was flagged. Live for subscribers, delayed on the free plan.' },
      { key: 'watchlist', href: '/watchlist', label: 'My Watchlist', Icon: I.Star,
        desc: 'The names you are tracking, with live prices and the alerts you set on them.' },
      { key: 'watchlist-scanner', href: '/watchlist-scanner', label: 'Watchlist Scanner', Icon: I.Crosshair,
        desc: 'A scanner that looks at only your watchlist, ranked by live relative volume with the names heating up on top. Your stocks, not the whole market.' },
      { key: 'profile', href: '/profile', label: 'Profile', Icon: I.User,
        desc: 'Your public page: your trades, your stats and what other members see when they follow you.' },
    ],
  },
  {
    // Discovery: today's ranked output and what is happening now, not tools you run yourself.
    id: 'moving', label: "What's moving now", items: [
      { key: 'stocks-today', href: '/stock-market-today', label: 'Stock Market Today', Icon: I.Sparkles,
        desc: 'One page for what the whole market is doing today: the tone, what is leading, and what changed since the open.' },
      { key: 'regulars', href: '/regulars', label: 'Regulars', Icon: I.History,
        desc: 'The names that keep coming back to the top of the scanner, ranked by how many of the last 10 sessions they held a top slot and filtered to real movers. A watchlist of persistent runners.' },
      { key: 'trending', href: '/trending', label: 'Trending Stocks', Icon: I.Flame,
        desc: 'What people are watching and talking about most today.' },
      { key: 'themes', href: '/themes', label: 'Stock Themes', Icon: I.Layers,
        desc: 'Names grouped by the story driving them, so you can see a whole move rather than one ticker.' },
      { key: 'next-session', href: '/next-session', label: 'Stocks to Watch', Icon: I.Target,
        desc: 'The shortlist for the next session, built after the close so you are not picking names at the open.' },
      { key: 'feeds', href: '/feeds', label: 'Feeds', Icon: I.Newspaper,
        desc: 'News and filings as they land, in one continuous stream.' },
      { key: 'recap', href: '/recap', label: 'Market Recaps', Icon: I.History,
        desc: 'What happened at the end of each session, written up so you can catch up in a minute.' },
    ],
  },
  {
    // Tools you point at the market yourself. The old Markets bucket's screener-shaped items
    // (Low Float, Betting Against, the Screener) join the scanners here so every "run a scan" surface
    // lives in one place.
    id: 'scanners', label: 'Scanners & screens', items: [
      { key: 'all-scanners', href: '/stock-scanners', label: 'All Scanners', Icon: I.Gauge,
        desc: 'Every scanner in one place: RVOL, halts, gaps, RSI, movers and a custom screener. Start here to see them all.' },
      { key: 'rvol-scanner', href: '/rvol-scanner', label: 'RVOL Scanner', Icon: I.Flame,
        desc: 'Stocks trading on far more volume than they normally would by this time of day.' },
      { key: 'gainers', href: '/biggest-gainers-today', label: 'Biggest Gainers', Icon: I.TrendingUp,
        desc: 'The largest percentage gains in the market today.' },
      { key: 'losers', href: '/biggest-losers-today', label: 'Biggest Losers', Icon: I.TrendingDown,
        desc: 'The largest percentage falls in the market today.' },
      { key: 'most-active', href: '/most-active-stocks', label: 'Most Active', Icon: I.ActivitySquare,
        desc: 'Where the most shares and the most dollars are changing hands.' },
      { key: 'premarket', href: '/premarket-movers', label: 'Premarket Movers', Icon: I.Sunrise,
        desc: 'What is already moving before the opening bell, and on what volume.' },
      { key: 'afterhours', href: '/after-hours-movers', label: 'After-Hours Movers', Icon: I.Moon,
        desc: 'What is moving after the close, usually on news or earnings.' },
      { key: 'gap-scanner', href: '/gap-scanner', label: 'Gap Scanner', Icon: I.ArrowDownUp,
        desc: 'Names opening well away from yesterday\'s close, with the size of the gap.' },
      { key: 'orb-scanner', href: '/orb-scanner', label: 'Opening Range Breakout', Icon: I.Rocket,
        desc: 'Stocks breaking above their first 15-minute range, and whether the breakout held or faded.' },
      { key: 'highs52', href: '/52-week-highs', label: '52-Week Highs', Icon: I.Award,
        desc: 'Stocks printing their highest price in a year, which is where sustained trends show up.' },
      { key: 'weekly', href: '/weekly-movers', label: 'Weekly Movers', Icon: I.CalendarRange,
        desc: 'The biggest moves of the week rather than the day, for trends that are still building.' },
      { key: 'ema-slope', href: '/ema-slope', label: 'EMA Slope Rankings', Icon: I.LineChart,
        desc: 'Names ranked by how steeply their trend is climbing, not just how far they have moved.' },
      { key: 'ema200', href: '/signals/ema-200', label: 'EMA-200 Signals', Icon: I.LineChart,
        desc: 'Names crossing their 200-day average, the line most traders treat as the trend divider.' },
      { key: 'accumulation', href: '/accumulation', label: 'Under Accumulation', Icon: I.Magnet,
        desc: 'Stocks being quietly bought before a move, scored 0-100 on seven price-and-volume signals.' },
      { key: 'low-float-movers', href: '/low-float-movers', label: 'Low Float Movers', Icon: I.Gauge,
        desc: 'Today\'s movers ranked by how few shares are actually available to trade, where small buying moves price furthest.' },
      { key: 'betting-against', href: '/betting-against', label: 'Betting Against', Icon: I.TrendingDown,
        desc: 'Where the crowd is positioned short, and which of those names are being squeezed.' },
      { key: 'halts', href: '/stock-halts-today', label: 'Stock Halts Today', Icon: I.Ban,
        desc: 'Which stocks were halted today, why, and when they resumed.' },
      { key: 'previousdayalerts', href: '/previousdayalerts', label: 'Yesterday\'s Alerts', Icon: I.History,
        desc: 'Yesterday\'s alerts with what each one did afterwards, so you can judge the signal instead of trusting it.' },
      { key: 'screener', href: '/screener', label: 'Stock Screener', Icon: I.Filter,
        desc: 'Build your own filter across price, volume, float and performance.' },
    ],
  },
  {
    // Everything you reach for once you have a name in mind.
    id: 'research', label: 'Research a stock', items: [
      { key: 'charts', href: '/chart', label: 'Charts', Icon: I.CandlestickChart,
        desc: 'Full charting with the indicators, filings and due diligence for a name on one screen.' },
      { key: 'compare', href: '/compare', label: 'Compare Stocks', Icon: I.ArrowDownUp,
        desc: 'Put two or more names side by side on performance, size and fundamentals.' },
      { key: 'due-diligence', href: '/due-diligence', label: 'Due Diligence', Icon: I.BookText,
        desc: 'The checks worth running on a company before you buy it, and the data to run them on.' },
      { key: 'news', href: '/news', label: 'News & Analysis', Icon: I.Newspaper,
        desc: 'Market news and our own write-ups, filtered to names that are actually moving.' },
    ],
  },
  {
    // Where the big money is positioned.
    id: 'flow', label: 'Money flow & institutions', items: [
      { key: 'money-map', href: '/money-map', label: 'The Money Map', Icon: I.MapIcon,
        desc: 'A single view of which sectors and sizes money is rotating into and out of.' },
      { key: 'smart-money', href: '/smart-money', label: 'Smart Money', Icon: I.Gem,
        desc: 'What institutions actually filed that they own, straight from their SEC disclosures.' },
      { key: 'super-portfolio', href: '/super-portfolio', label: 'Super Portfolio', Icon: I.Layers,
        desc: 'Every tracked institution combined into one book, so you can see the positions they hold in common.' },
      { key: 'investors', href: '/investors', label: 'Famous Investors', Icon: I.Users,
        desc: 'The holdings of well-known investors, and what they bought or sold last quarter.' },
      { key: 'sector-performance', href: '/sector-performance', label: 'Sector Performance', Icon: I.Gauge,
        desc: 'Which sectors are leading and lagging today, this week and this year.' },
    ],
  },
  {
    // What the crowd is doing, on the site and off it. The retail-attention reads (Reddit, Google)
    // sit with the member community rather than lost among the market pages.
    id: 'community', label: 'Community & buzz', items: [
      { key: 'reddit-radar', href: '/reddit-radar', label: 'Reddit Radar', Icon: I.Flame,
        desc: 'Which tickers retail is talking about, and whether the tone is bullish or bearish.' },
      { key: 'google-searches', href: '/google-searches', label: 'Google Searches', Icon: I.Search,
        desc: 'Search interest by ticker, an early read on attention before it shows up in volume.' },
      { key: 'chat', href: '/chat', label: 'Chat', Icon: I.MessageSquare,
        desc: 'The live room. Talk to other members while the market is moving.' },
      { key: 'messages', href: '/messages', label: 'Messages', Icon: I.Mail,
        desc: 'Your private conversations with other members.' },
      // ⭐ THE DESCRIPTION THE OWNER CORRECTED: this page finds PEOPLE, not stocks. Its label alone
      // reads as stock discovery on a stock site, and it was documented nowhere before 2026-08-15.
      { key: 'discover', href: '/discover', label: 'Discover', Icon: I.Users,
        desc: 'Find other members. Every action on the site is logged here, so you can see who is trading well and follow them.' },
      { key: 'rankings', href: '/rankings', label: 'Rankings', Icon: I.Trophy,
        desc: 'The leaderboard. Who is contributing most and whose calls are landing.' },
      { key: 'game', href: '/game', label: 'Game', Icon: I.Gamepad2,
        desc: 'Practice picking without money on the line, and see how you score against everyone else.' },
    ],
  },
  {
    // Dated events plus the macro backdrop. The old "Rates & Crypto" folds in here: they are all
    // "the wider clock", not stock lists.
    id: 'calendars', label: 'Calendars & macro', items: [
      { key: 'earnings', href: '/earnings-calendar', label: 'Earnings Calendar', Icon: I.CalendarDays,
        desc: 'Who reports and when, so a position is never a surprise the morning after.' },
      { key: 'economic', href: '/economic-calendar', label: 'Economic Calendar', Icon: I.Landmark,
        desc: 'The data releases that move the whole market, and when they land.' },
      { key: 'calendar', href: '/calendar', label: 'Market Calendar', Icon: I.CalendarDays,
        desc: 'Trading days, holidays and shortened sessions.' },
      { key: 'dividends', href: '/dividends', label: 'Dividend Calendar', Icon: I.Coins,
        desc: 'Upcoming ex-dividend and payment dates, with the yield each one is paying.' },
      { key: 'ipos', href: '/ipos', label: 'IPO Calendar', Icon: I.Rocket,
        desc: 'New listings that are coming, pricing, and how recent ones have traded.' },
      { key: 'crypto', href: '/crypto', label: 'Crypto Prices', Icon: I.Bitcoin,
        desc: 'Live prices for the major coins.' },
      { key: 'currency', href: '/currency', label: 'Currency Converter', Icon: I.Banknote,
        desc: 'Convert between currencies at current rates.' },
      { key: 'mortgage', href: '/mortgage-rates', label: 'Mortgage Rates', Icon: I.Percent,
        desc: 'Current mortgage rates and how they have moved.' },
    ],
  },
  {
    id: 'trade', label: 'Trade & Track', items: [
      { key: 'pricealerts', href: '/alerts/price', label: 'Price Alerts', Icon: I.BellRing,
        desc: 'Set a price on any name and get told when it gets there.' },
      { key: 'trades', href: '/trades', label: 'Trades', Icon: I.Wallet,
        desc: 'Your trade journal. Log entries and exits and see your own results honestly.' },
      { key: 'strategy', href: '/strategy', label: 'Strategy', Icon: I.Compass,
        desc: 'Build and save the screens and rules you trade, then reuse them.' },
      { key: 'ai-journal', href: '/ai-journal', label: 'AI Journal', Icon: I.BookText,
        desc: 'The trading agent\'s own log: what it took, why, and how it turned out.' },
      { key: 'call-record', href: '/call-record', label: 'Our Call Record', Icon: I.Receipt,
        desc: 'Every name we posted publicly and what it did in the hour after, losers included.' },
      { key: 'calculators', href: '/calculators', label: 'Calculators', Icon: I.Calculator,
        desc: 'Position size, break even, averaging down, dividends and compounding, all in one place.' },
    ],
  },
  {
    // Learn the site and the vocabulary, plus the outward-facing pages (API, partners, donate).
    id: 'learn', label: 'Learn & more', items: [
      { key: 'siteguide', href: '/site-guide', label: 'Site Guide', Icon: I.Compass,
        desc: 'What every page on this site is for and when to use it. Start here if you are new.' },
      { key: 'guides', href: '/guides', label: 'Guides', Icon: I.Book,
        desc: 'Plain-English explainers on halts, floats, short interest and the rest of the vocabulary.' },
      // Moved here from Trade & Track 2026-10-30 (full-nav trim): it is the "should I trust the alerts"
      // page, step 2 of the first-week plan, and 89 signed-in people in 60 days. Every other Trade &
      // Track link drew under 20, so it would otherwise have been a heading over one link.
      { key: 'track-record', href: '/track-record', label: 'Track Record', Icon: I.Award,
        desc: 'The measured edge: how much more often a flagged stock moves than a random one, with the downside shown and every session tested. Market data, never trade P&L.' },
      { key: 'taxes', href: '/taxes', label: 'Trading Taxes', Icon: I.Receipt,
        desc: 'How trading gains are taxed, wash sales, and what to keep records of.' },
      { key: 'apidocs', href: '/api-docs', label: 'API Docs', Icon: I.Code,
        desc: 'Pull our rankings into your own tools. Public, so you can read it before you sign up.' },
      { key: 'devs', href: '/developers', label: 'Developers', Icon: I.Key,
        desc: 'Your API keys and usage.' },
      { key: 'creators', href: '/stock-content-creators', label: 'Content Creators', Icon: I.Users,
        desc: 'For people who write or stream about stocks and want to use our data.' },
      { key: 'partners', href: '/affiliate', label: 'Partner Program', Icon: I.Gift,
        desc: 'Earn by referring members. Two tiers, paid monthly.' },
      { key: 'support', href: '/support', label: 'Support', Icon: I.HelpCircle,
        desc: 'Ask a question or report something broken, and see what has already been answered.' },
      { key: 'roadmap', href: '/roadmap', label: 'Roadmap', Icon: I.Route,
        desc: 'What is being built next, and what shipped recently.' },
      { key: 'donate', href: '/donate', label: 'Donate', Icon: I.Heart,
        desc: 'Support the site directly. Donations add premium days to your account.' },
    ],
  },
  {
    id: 'admin', label: 'Admin', admin: true, items: [
      { key: 'status', href: '/status', label: 'Status', Icon: I.ActivitySquare, desc: 'System health and service status.' },
      { key: 'watcher', href: '/watcher', label: 'Watcher', Icon: I.Eye, desc: 'The live trading agent and its open positions.' },
      { key: 'watcher-lab', href: '/watcher-lab', label: 'Watcher Lab', Icon: I.FlaskConical, desc: 'Deploy log and experiment results for the agent.' },
      { key: 'growth', href: '/growth', label: 'Growth', Icon: I.LineChart, desc: 'Signups, retention and conversion.' },
      { key: 'site-tracking', href: '/site-tracking', label: 'Site Tracking', Icon: I.Radar, desc: 'Traffic, referrers and page-level analytics.' },
      // ⚠ WAS REACHABLE ONLY BY TYPING THE URL. The console shipped 2026-11-02 and was never linked
      // from the nav or the user dropdown, so the owner went looking for it under Site Tracking and
      // could not find it (2026-11-04). Site Tracking is on-site analytics; this is the SEARCH side.
      { key: 'seo-console', href: '/admin/seo', label: 'SEO Console', Icon: I.Search, desc: 'Google clicks, impressions and ranking over time, with the deploy and annotation timeline beside them.' },
      { key: 'user-admin', href: '/user-admin', label: 'User Admin', Icon: I.UserCog, desc: 'Look up a member, cancel their subscription, or permanently delete their account.' },
      { key: 'integrations', href: '/admin/integrations', label: 'Integrations', Icon: I.Plug, desc: 'Keys and switches the site reads live. Saving takes effect with no deploy.' },
      { key: 'video-review', href: '/video-review', label: 'Video Review', Icon: I.Clapperboard, desc: 'Watch the rendered Shorts, approve, skip or download them before they post.' },
      { key: 'music-review', href: '/music-review', label: 'Music Review', Icon: I.Music, desc: 'Listen to every song the Shorts use and mark each Keep, Drop or Unsure. A Drop leaves it out of the next render.' },
      { key: 'support-dashboard', href: '/support-dashboard', label: 'Support Dashboard', Icon: I.LifeBuoy, desc: 'Open support threads and response times.' },
      { key: 'reddit-posts', href: '/reddit-posts', label: 'Reddit Posts', Icon: I.MessageCircle, desc: 'What the auto-poster published and how it performed.' },
      { key: 'subreddits', href: '/admin/subreddits', label: 'Reddit Subreddits', Icon: I.MessageCircle, desc: 'Subreddit targeting for the poster.' },
      { key: 'reddit-queue', href: '/reddit-queue', label: 'Reddit Post Queue', Icon: I.MessageCircle, desc: 'Ready-to-post Top Play, recaps and articles, hand-posted from here.' },
      { key: 'x-queue', href: '/x-queue', label: 'X Post Queue', Icon: I.Send, desc: 'Pending and published posts on X.' },
      { key: 'inbox', href: '/inbox', label: 'Interaction Inbox', Icon: I.Inbox, desc: 'Replies and mentions on your Reddit and X posts, open vs handled.' },
      { key: 'ai-config', href: '/ai-config', label: 'AI Algo Config', Icon: I.Settings, desc: 'Model and prompt configuration.' },
      { key: 'alpaca', href: '/alpaca', label: 'Alpaca Dashboard', Icon: I.Briefcase, desc: 'Live broker account and fills.' },
      { key: 'alpacapaper', href: '/alpacapaper', label: 'Paper Dashboard', Icon: I.FileSpreadsheet, desc: 'Paper broker account and fills.' },
      { key: 'articles', href: '/admin/articles', label: 'Articles & News', Icon: I.Newspaper, desc: 'Write and publish articles.' },
      { key: 'affiliates', href: '/admin/affiliates', label: 'Affiliate Applications', Icon: I.Users, desc: 'Review partner applications.' },
      { key: 'logs', href: '/admin/logs', label: 'Log Review', Icon: I.ScrollText, desc: 'Service logs and errors.' },
    ],
  },
] as const

/** The stable per-item id. Persisted in localStorage, so it must never change. See the header. */
export type NavKey = (typeof SIDEBAR_GROUPS)[number]['items'][number]['key']

export type NavItemView = {
  key: NavKey
  href: string
  label: string
  Icon: LucideIcon
  desc: string
}
export type NavGroupView = {
  id: string
  label: string | null
  admin?: boolean
  items: NavItemView[]
}

/** Every item, flattened, in render order. */
export const NAV_ITEMS: NavItemView[] =
  SIDEBAR_GROUPS.flatMap((g) => g.items.map((i) => ({ ...i })) as unknown as NavItemView[])

/**
 * The groups a given viewer should see.
 *
 * ⚠ Admin is gated at the GROUP level because it is the only wholly-admin group. Rendering it to an
 * ordinary user would put 15 internal links in the public sidebar, so the filter drops the group
 * rather than hiding items inside it and leaving an empty heading.
 */
export function sidebarGroupsFor(opts: { isAdmin?: boolean }): NavGroupView[] {
  return SIDEBAR_GROUPS
    .filter((g) => (('admin' in g && g.admin) ? !!opts.isAdmin : true))
    .map((g) => ({
      id: g.id,
      label: g.label,
      admin: 'admin' in g ? g.admin : undefined,
      items: g.items.map((i) => ({ ...i })) as unknown as NavItemView[],
    }))
}

/** Default sidebar order: every key, in declaration order. */
export const defaultNavOrder = (): NavKey[] =>
  SIDEBAR_GROUPS.flatMap((g) => g.items.map((i) => i.key)) as unknown as NavKey[]

/**
 * THE STARTER VIEW: the few links a brand-new trader should meet, in the order they should meet
 * them.
 *
 * 🚨 THE PROBLEM. This model holds 76 non-admin items across 9 groups (2026-10-30; count it, do not trust this line). That is correct as an
 * INVENTORY and hostile as a FIRST IMPRESSION: the owner's own words when the model was extracted
 * were "site is too many pages and unorganized, users don't know where to go". Consolidating the
 * three disagreeing lists fixed correctness; regrouping by intent (2026-09-21) made the map
 * scannable; but neither made the first visit smaller.
 *
 * ⭐ THIS IS AN ORDERED PATH, NOT A FILTERED SUBSET, and the order is the whole point. It mirrors
 * the beginner arc the owner already curated on /site-guide ("New here? Do these five, in order"):
 * get oriented, see today's ranked output, learn to scan yourself, then let it come to you. The two
 * lists are meant to agree, and a test asserts the guide's five all appear here.
 *
 * ⛔ IT IS AN EXPLICIT LIST, NOT A `starter: true` FLAG ON EACH ITEM. A flag scattered across 69
 * declarations cannot be reviewed as a curation decision; this can be read in ten seconds and
 * argued with. It is typed `NavKey[]`, so a key that does not exist, or is renamed, FAILS
 * TYPECHECK rather than silently vanishing from the beginner's sidebar.
 *
 * ⚠ ADVANCED TRADERS MUST LOSE NOTHING. This is a default for people with no saved preferences, and
 * everything is one click away behind "Show all". See STARTER_MODE_DEFAULT_RULE in WatchlistPane.
 *
 * ✂ TRIMMED 12 -> 9 on 2026-10-30 (owner: "trim the sidebar starter view"), BY MEASURED USE, not taste.
 * nav_starter_events, 60 days, 997 browsers that clicked a starter link: top-stocks 457 · stocks-today
 * 392 · radar 356 · rvol-scanner 344 · screener 315 · all-scanners 299 · home 292 · alerts 211 ·
 * charts 159 · watchlist 121 · siteguide 51 · guides 48. Cut: guides and siteguide (5% each; the
 * "Show all" button drew 179 browsers, so IT is the route to everything else, not the guide link) and
 * charts (a chart is reached by clicking a symbol; /chart drew 65 of 547 new signups in week 1).
 * watchlist stays despite ranking 10th: it is step 5 of the first-week plan and a member feature.
 * Register: starter-nav-trim-1030 (the read that says whether the cut cost anyone).
 */
export const STARTER_KEYS: NavKey[] = [
  'home',           // where am I (a signed-in member lands on The Pulse)
  'stocks-today',   // what is the market doing right now
  'top-stocks',     // six names already ranked, with the reason each made it
  'radar',          // what the Watcher is flagging right now, live
  'alerts',         // the thing that tells you without you watching
  'watchlist',      // keep the names you care about
  'all-scanners',   // see every scanner in one place
  'rvol-scanner',   // now run the scan yourself
  'screener',       // and filter it your own way
]

/** The starter items, in starter order. Items are looked up by key so the order here wins. */
export function starterNavItems(): NavItemView[] {
  const byKey = new Map(NAV_ITEMS.map((i) => [i.key, i]))
  return STARTER_KEYS.map((k) => byKey.get(k)).filter((i): i is NavItemView => !!i)
}

/**
 * THE FULL NAV, TRIMMED BY USE (2026-10-30, owner: "trim the full 76-item nav too").
 *
 * 📏 THE MEASUREMENT. Distinct SIGNED-IN people per link, page_views, 60 days to 2026-10-30 (signed-in
 * is the only count a scraper cannot fake: see most-used-surfaces-are-not-what-the-logs-say-1029).
 * The top ten drew 111 to 450 each. These 41 drew under 20 each, or were the lone survivor of a group
 * (a heading over one link is clutter, not organisation): Smart Money 23, Reddit Radar 31, Market
 * Calendar 33. The numbers are on each line so the next trim argues with data, not with memory.
 *
 * ⭐ NOTHING IS REMOVED. These render in a collapsed "More pages" section at the foot of every menu.
 * No SEO cost either way: both menus render client-side only (checked 10-30, the server HTML holds no
 * nav headings), and /site-guide links every page in its body. SIDEBAR_GROUPS is
 * untouched, so ⌘K search, the site guide and each user's saved order and hidden list are unchanged.
 * The section remembers being opened, and opens itself when the current page is one of these.
 *
 * ⛔ An explicit list, for the same reason as STARTER_KEYS: it can be read and argued with in ten
 * seconds, and a renamed key fails typecheck instead of silently falling back into the main list.
 * Register: full-nav-trim-1030.
 */
export const MORE_KEYS: NavKey[] = [
  'recap',              // 16
  'halts',              // 19 (search traffic finds it directly: 118 multi-page anonymous sessions)
  'ema-slope',          // 15
  'losers',             // 13
  'ema200',             // 12
  'highs52',            // 10
  'previousdayalerts',  // 9
  'weekly',             // 8
  'compare',            // 10
  'smart-money',        // 23, lone survivor of its group
  'money-map',          // 18
  'super-portfolio',    // 18
  'investors',          // 14
  'sector-performance', // 11
  'reddit-radar',       // 31, lone survivor of its group
  'messages',           // 19 (also in the top bar)
  'google-searches',    // 14
  'rankings',           // 14
  'game',               // 13
  'chat',               // 8
  'discover',           // 6
  'calendar',           // 33, lone survivor of its group
  'economic',           // 10
  'earnings',           // 9
  'ipos',               // 8
  'crypto',             // 8
  'dividends',          // 5
  'currency',           // 4
  'mortgage',           // 2
  'pricealerts',        // 11 (also reachable from /alerts)
  'trades',             // 15
  'strategy',           // 13
  'ai-journal',         // 5
  'call-record',        // 9
  'calculators',        // 5
  'partners',           // 17 (also "Refer and earn" in the account menu)
  'apidocs',            // 11
  'roadmap',            // 8
  'devs',               // 6
  'creators',           // 5
  'taxes',              // 4
]

/**
 * Split a viewer's groups into the main menu and the "More pages" section, keeping group order and
 * labels in both. A group left empty on either side is dropped, so no heading ever sits over nothing.
 * Menus call this on the result of sidebarGroupsFor(), which stays the one source of what exists.
 */
export function splitNavByUse(groups: NavGroupView[]): { main: NavGroupView[]; more: NavGroupView[] } {
  const more = new Set<string>(MORE_KEYS)
  const pick = (keep: (k: string) => boolean) => groups
    .map((g) => ({ ...g, items: g.items.filter((i) => keep(i.key)) }))
    .filter((g) => g.items.length > 0)
  return { main: pick((k) => !more.has(k)), more: pick((k) => more.has(k)) }
}

/**
 * Where a link should point for this viewer. /profile is the only session-dependent target; the
 * hand-written menus wrote it as `user ? \`/profile/${user.username}\` : '/auth'` and that behaviour
 * is preserved rather than quietly changed by the extraction.
 */
export function navHref(item: { href: string }, opts: { signedIn?: boolean; username?: string | null }): string {
  if (item.href === '/profile') return opts.signedIn && opts.username ? `/profile/${opts.username}` : '/auth'
  return item.href
}
